import React, { useRef } from 'react';
import { useDebounce } from 'react-use';
import { TiptapEditor } from '../⚛️atoms/TiptapEditor';
import { OneLineInput } from '../⚛️atoms/OneLineInput';
import { getDataAPI } from '../../Helpers/FetchAPI';
import toast from 'react-hot-toast';
import { MarkdownWrapper } from './MarkdownWrapper';
import { getTranslation, updateTranslation } from '../../utils/translationHelper';
import useLockedTranslate from '../../Hooks/useLockedTranslate';
import { useWorkbookV2EditorGridResize } from '../../Hooks/useWorkbookV2EditorGridResize';
import { TranslateButton } from '../atoms/translateButton';
import { cloneWorkbookV2Value } from './editorUtils';

export const WorkbookV2Content = ({ item, updateItem, editing, editingLanguage = null, workbook }) => {
  const oldQuestionInitializedRef = useRef(false);
  const { lang } = useLockedTranslate(workbook);
  const { editGrid, gridCols } = useWorkbookV2EditorGridResize();

  const updateEditorItem = (updates) => {
    const nextItem = cloneWorkbookV2Value(item);
    nextItem.text = updates.text ?? nextItem.text;
    nextItem.page_number = updates.page_number ?? nextItem.page_number;
    nextItem.old_question_id = updates.old_question_id ?? nextItem.old_question_id;
    nextItem.translations = updates.translations ?? nextItem.translations;
    updateItem(nextItem);
  };

  useDebounce(
    () => {
      if (!editing) {
        return;
      }

      if (!oldQuestionInitializedRef.current) {
        oldQuestionInitializedRef.current = true;
        return;
      }

      if (item.old_question_id) {
        toast.promise(
          getDataAPI(`/workbookItems/question/${item.old_question_id}`).then((response) => {
            const question = response.data.questions.find((currentQuestion) => currentQuestion._id === item.old_question_id);
            updateEditorItem({ text: question.title });
            return question;
          }),
          {
            loading: 'Getting Old Question Data',
            success: (question) => `The old Question was a ${question.type}`,
            error: 'Error getting old question data'
          },
          { id: 'old-question' }
        );
      }
    },
    250,
    [editing, item.old_question_id]
  );

  return (
    <div className={`flex w-full items-center gap-2 ${editing && 'border-pdteal rounded-lg border-2 px-8 py-12'}`}>
      {!editing && (
        <p>
          <MarkdownWrapper>{lang === 'en' ? item.text : getTranslation(item.translations, lang, 'text', item.text)}</MarkdownWrapper>
        </p>
      )}
      {editing && (
        <div ref={editGrid} className={`grid w-full ${gridCols} gap-8 transition-all duration-300 ease-out`}>
          <div className={`relative ${!editingLanguage && 'col-span-2'}`}>
            <p className="mb-2 text-xl/6 font-medium text-gray-900">Content</p>
            <TiptapEditor content={item.text} onChange={(value) => updateEditorItem({ text: value })} />
            {editingLanguage && (
              <TranslateButton
                text={item.text}
                language={editingLanguage}
                onResponse={(value) =>
                  updateEditorItem({
                    translations: updateTranslation(item.translations ?? {}, editingLanguage, 'text', value)
                  })
                }
              />
            )}
          </div>
          {editingLanguage && (
            <div>
              <p className="mb-2 text-xl/6 font-medium text-gray-900">Content ({editingLanguage})</p>
              <TiptapEditor
                content={getTranslation(item.translations, editingLanguage, 'text')}
                onChange={(value) =>
                  updateEditorItem({
                    translations: updateTranslation(item.translations ?? {}, editingLanguage, 'text', value)
                  })
                }
              />
            </div>
          )}
          <div className="col-start-1 w-2/5">
            <OneLineInput
              value={item.page_number}
              label="Page Number"
              onChange={(value) => updateEditorItem({ page_number: value })}
            />
          </div>
          <OneLineInput
            value={item.old_question_id}
            label="Old Question ID"
            onChange={(value) => updateEditorItem({ old_question_id: value })}
          />
        </div>
      )}
    </div>
  );
};
